import '../../styles/tv-entry.css';
import { useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { TvSafeArea } from '../../components/display/TvSafeArea';
import { useFullscreen } from '../../hooks/useFullscreen';
import {
  collectTvDiagnostics,
  isTvAcceptanceEnabled,
  normalizeInset,
  readDisplaySample,
  readTvInset,
  TV_REPORT_KEY,
  writeTvInset,
} from '../../lib/tvAcceptance';

const steps = [
  [
    'edges',
    '四邊與文字',
    '先以 0% 觀察四角、外框與圓形，再調整到完整可見。若外框完整但賽事內部裁切，記為未通過並拍照。',
  ],
  [
    'pairing',
    '配對與即時畫面',
    '在 staging 管理頁建立測試畫面、綁定測試賽事、取得配對碼。此 TV 前往配對頁輸入；確認場館、畫面、賽事、倒數與管理端同步。',
  ],
  [
    'replay',
    '配對碼不可重用',
    '在另一個無既有配對的測試瀏覽器重用同一配對碼，必須被拒絕。不要將有效碼寫入報告。',
  ],
  [
    'relaunch',
    '關閉後重新開啟',
    '關閉並重開實際使用的 APK／瀏覽器；應保留配對、校正，回到同一場館與賽事且持續更新。',
  ],
  [
    'reboot',
    '裝置斷電重啟',
    '實際重新啟動 TV Stick，等待網路恢復並開啟畫面；確認配對、校正與即時更新，記錄啟動耗時。網頁無法代替真實重啟。',
  ],
  [
    'revocation',
    '撤銷與重新配對',
    '管理端按「撤銷既有連線」，舊畫面須失效；重新開啟也不可恢復。新碼可重新配對，舊碼仍不可用。',
  ],
] as const;
type Result = 'pending' | 'pass' | 'fail';
type Draft = { device: string; notes: string; results: Record<string, Result> };
const emptyDraft = (): Draft => ({ device: '', notes: '', results: {} });

function readDraft(): Draft {
  try {
    const parsed = JSON.parse(localStorage.getItem(TV_REPORT_KEY) ?? 'null');
    if (!parsed || typeof parsed !== 'object' || parsed.webRevision !== __GIT_REVISION__)
      return emptyDraft();
    return {
      device: typeof parsed.device === 'string' ? parsed.device.slice(0, 160) : '',
      notes: typeof parsed.notes === 'string' ? parsed.notes.slice(0, 2000) : '',
      results: Object.fromEntries(
        steps.map(([id]) => [
          id,
          ['pass', 'fail'].includes(parsed.results?.[id]) ? parsed.results[id] : 'pending',
        ]),
      ),
    };
  } catch {
    return emptyDraft();
  }
}

export default function TVAcceptance() {
  if (!isTvAcceptanceEnabled) return <Navigate to="/tv" replace />;
  return <AcceptancePage />;
}

function AcceptancePage() {
  const [inset, setInset] = useState(readTvInset);
  const [draft, setDraft] = useState(readDraft);
  const [pattern, setPattern] = useState(false);
  const [notice, setNotice] = useState('');
  const [report, setReport] = useState('');
  const [diagnostics, setDiagnostics] = useState(collectTvDiagnostics);
  const patternExit = useRef<HTMLButtonElement>(null);
  const { isFullscreen, isSupported, toggleFullscreen } = useFullscreen();

  useEffect(() => {
    const refresh = () => setDiagnostics(collectTvDiagnostics());
    window.addEventListener('resize', refresh);
    document.addEventListener('fullscreenchange', refresh);
    document.addEventListener('webkitfullscreenchange', refresh);
    window.visualViewport?.addEventListener('resize', refresh);
    refresh();
    return () => {
      window.removeEventListener('resize', refresh);
      document.removeEventListener('fullscreenchange', refresh);
      document.removeEventListener('webkitfullscreenchange', refresh);
      window.visualViewport?.removeEventListener('resize', refresh);
    };
  }, [inset, pattern]);

  useEffect(() => {
    if (pattern) patternExit.current?.focus();
  }, [pattern]);

  // Linear D-pad navigation also works in ordinary browsers without spatial navigation.
  function navigateWithRemote(event: React.KeyboardEvent<HTMLElement>) {
    if (event.key === 'Escape' || event.key === 'BrowserBack') {
      if (pattern) {
        event.preventDefault();
        setPattern(false);
      }
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    const target = event.target as HTMLElement;
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
    const controls = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>(
        'button:not(:disabled), a[href], input, textarea, select',
      ),
    );
    const current = controls.indexOf(target);
    const next = Math.max(
      0,
      Math.min(
        controls.length - 1,
        current + (['ArrowDown', 'ArrowRight'].includes(event.key) ? 1 : -1),
      ),
    );
    event.preventDefault();
    controls[next]?.focus();
  }

  function saveDraft(next: Draft) {
    setDraft(next);
    setReport('');
    try {
      const saved = JSON.stringify({ ...next, webRevision: __GIT_REVISION__ });
      localStorage.setItem(TV_REPORT_KEY, saved);
      if (localStorage.getItem(TV_REPORT_KEY) !== saved) throw new Error();
    } catch {
      setNotice('無法保存驗收紀錄。離開前請產生報告並拍照或複製。');
    }
  }

  function saveInset(value: number) {
    const next = normalizeInset(value);
    setInset(next);
    setReport('');
    const saved = writeTvInset(next);
    setNotice(
      saved
        ? `已保存每邊 ${next}% 留白；套用到此瀏覽器的 staging 賽事畫面。`
        : '保存失敗；目前僅為預覽，無法套用至賽事畫面。',
    );
    // Changing calibration invalidates visual/persistence judgments made with the old setting.
    saveDraft({
      ...draft,
      results: { ...draft.results, edges: 'pending', relaunch: 'pending', reboot: 'pending' },
    });
  }

  function buildReport() {
    const results = steps.map(([id, label]) => ({
      id,
      label,
      result: draft.results[id] ?? 'pending',
    }));
    const payload = {
      schemaVersion: 1,
      ...collectTvDiagnostics(),
      lastDisplaySample: readDisplaySample(),
      deviceLabel: draft.device,
      notes: draft.notes,
      results,
      allManualChecksPassed: results.every((row) => row.result === 'pass'),
      evidenceType: 'operator-reported; not automatic physical acceptance',
      limitations:
        'Browser cannot verify Android API level, manufacturer, physical HDMI mode or overscan. No production readiness verdict.',
    };
    const text = JSON.stringify(payload, null, 2);
    setReport(text);
    setNotice('報告已產生，未上傳。可拍照、複製文字或下載 JSON；不要填寫配對碼、密碼或完整序號。');
  }

  const button =
    'rounded-lg border-2 border-slate-500 bg-slate-800 px-5 py-3 text-lg focus:outline focus:outline-4 focus:outline-yellow-300';
  const calibration = (
    <div className="flex flex-wrap items-center justify-center gap-3">
      <button className={button} disabled={inset <= 0} onClick={() => saveInset(inset - 1)}>
        減少留白
      </button>
      <strong className="text-xl" aria-live="polite">
        每邊 {inset}%
      </strong>
      <button className={button} disabled={inset >= 10} onClick={() => saveInset(inset + 1)}>
        增加留白
      </button>
      <button className={button} onClick={() => saveInset(0)}>
        重設 0%
      </button>
    </div>
  );

  if (pattern)
    return (
      <section className="tv-entry" onKeyDown={navigateWithRemote} aria-label="四邊校正測試">
        <TvSafeArea inset={inset}>
          <div
            className="tv-entry-pattern h-full w-full relative border-8 border-yellow-300 bg-slate-950 text-white"
            style={{ boxSizing: 'border-box' }}
          >
            {['左上', '右上', '左下', '右下'].map((label, index) => (
              <span
                key={label}
                style={{
                  position: 'absolute',
                  top: index < 2 ? 8 : undefined,
                  bottom: index >= 2 ? 8 : undefined,
                  left: index % 2 === 0 ? 8 : undefined,
                  right: index % 2 === 1 ? 8 : undefined,
                  fontSize: 'clamp(16px, 3vw, 40px)',
                }}
              >
                {label}
              </span>
            ))}
            <div
              className="absolute flex flex-col items-center justify-center gap-3"
              style={{ inset: '10%' }}
            >
              <p className="text-xl">請確認黃色外框、四角文字與圓形完整可見</p>
              <div
                className="tv-entry-circle border-4 border-cyan-300 rounded-full flex items-center justify-center"
                style={{ width: '18vh', height: '18vh', flexShrink: 0 }}
              >
                圓形
              </div>
              <div
                className="font-mono border-4 border-white rounded-xl px-5"
                style={{ fontSize: 'clamp(24px, 6vh, 80px)' }}
              >
                12:34
              </div>
              {calibration}
              <button ref={patternExit} className={button} onClick={() => setPattern(false)}>
                返回驗收流程
              </button>
            </div>
          </div>
        </TvSafeArea>
      </section>
    );

  return (
    <main
      onKeyDown={navigateWithRemote}
      className="tv-entry min-h-screen bg-slate-950 text-white"
      style={{ padding: '5vh 7vw' }}
    >
      <div className="mx-auto max-w-5xl space-y-6">
        <h1 className="text-3xl font-bold">TV 實機驗收與畫面校正</h1>
        <p className="text-lg">
          請在 TV
          實際使用的瀏覽器開啟。資料只存於此瀏覽器，不會自動上傳；換瀏覽器或清除資料後須重新驗收。
        </p>
        <p>
          瀏覽器無法確認 Android API level、製造商、HDMI 實際輸出解析度或電視
          overscan；請另附系統資訊與完整螢幕照片。照片顯示的版本僅為裝置宣告值。
        </p>
        <div className="flex flex-wrap gap-3">
          <button autoFocus className={button} onClick={() => setPattern(true)}>
            1. 開啟四邊測試框
          </button>
          <button
            className={button}
            disabled={!isSupported}
            onClick={() =>
              void toggleFullscreen().catch(() =>
                setNotice('全螢幕請求失敗，請使用瀏覽器或 TV 的全螢幕設定。'),
              )
            }
          >
            {isFullscreen ? '退出全螢幕' : '進入全螢幕'}
          </button>
          <Link className={button} to="/tv">
            2. 前往配對
          </Link>
          <Link className={button} to="/tv/paired">
            3. 查看已配對賽事
          </Link>
        </div>
        <p>
          遙控器方向鍵移動按鈕焦點，OK 確認。輸入欄位依瀏覽器鍵盤操作；可用
          Tab／Shift+Tab。測試框選「返回驗收流程」或按 Escape 回到流程。
        </p>
        <section className="space-y-3" aria-label="畫面校正">
          <h2 className="text-2xl">畫面裁切檢查</h2>
          <p>
            先查看 TV 的「原始尺寸／Just Scan／Screen Fit」設定。外框被切掉時逐步增加留白；最多每邊
            10%，等比例縮小整個畫面。這不會修改場館版面，也不會修復版面內部溢位。
          </p>
          {calibration}
          <p>
            設定僅套用此瀏覽器、此網站。完成校正後重新開啟已配對賽事，確認真正的時鐘外框與文字。調整留白會重設四邊、relaunch、reboot
            的驗收結果。
          </p>
          <p>
            在賽事畫面開啟右下角齒輪，選「TV
            實機驗收與畫面校正」可帶回該畫面的尺寸量測；報告會附量測時間，舊量測不代表目前狀態。
          </p>
        </section>
        <p role="status" className="text-yellow-300">
          {notice}
        </p>
        <section className="space-y-3">
          <h2 className="text-2xl">設備與瀏覽器量測</h2>
          <p>
            視窗：{diagnostics.window.width} × {diagnostics.window.height} CSS px；screen：
            {diagnostics.screen.width} × {diagnostics.screen.height}；DPR：
            {diagnostics.window.devicePixelRatio}。這些數值不等於面板原生解析度。
          </p>
          <p className="break-all">{diagnostics.userAgent}</p>
          <label className="block">
            設備標記（如 Z1 AIR／Fire TV K3R6AT，不含完整序號）
            <input
              className="block w-full bg-slate-800 border p-3"
              maxLength={160}
              value={draft.device}
              onChange={(e) => saveDraft({ ...draft, device: e.target.value })}
            />
          </label>
        </section>
        <section className="space-y-4">
          <h2 className="text-2xl">逐項實機驗收</h2>
          <p>
            請逐項操作後才標記結果。離開此頁前紀錄會保存；返回同一頁繼續。全部通過仍不代表 R-16 或
            production 已核准。
          </p>
          {steps.map(([id, label, hint]) => (
            <fieldset key={id} className="border border-slate-600 p-4 space-y-3">
              <legend className="text-xl">{label}</legend>
              <p>{hint}</p>
              <div className="flex flex-wrap gap-3">
                {(['pending', 'pass', 'fail'] as const).map((value, index) => (
                  <button
                    key={value}
                    className={button}
                    aria-pressed={(draft.results[id] ?? 'pending') === value}
                    style={{
                      borderColor:
                        (draft.results[id] ?? 'pending') === value ? '#facc15' : undefined,
                    }}
                    onClick={() =>
                      saveDraft({ ...draft, results: { ...draft.results, [id]: value } })
                    }
                  >
                    {['未測試', '通過', '未通過'][index]}
                  </button>
                ))}
              </div>
            </fieldset>
          ))}
        </section>
        <label className="block">
          觀察紀錄（裁切方向、TV 顯示設定、啟動耗時；不含密碼或配對碼）
          <textarea
            className="block w-full bg-slate-800 border p-3"
            rows={4}
            maxLength={2000}
            value={draft.notes}
            onChange={(e) => saveDraft({ ...draft, notes: e.target.value })}
          />
        </label>
        <div className="flex flex-wrap gap-3">
          <button className={button} onClick={buildReport}>
            產生驗收報告
          </button>
          <button
            className={button}
            onClick={() => {
              saveDraft(emptyDraft());
              setNotice('驗收結果已重設為未測試，校正值保留。');
            }}
          >
            清除本次驗收結果
          </button>
        </div>
        {report && (
          <section className="space-y-3">
            <label className="block">
              驗收報告 JSON
              <textarea
                readOnly
                className="block w-full bg-slate-800 border p-3 font-mono"
                rows={12}
                value={report}
              />
            </label>
            <button
              className={button}
              onClick={() => {
                const url = URL.createObjectURL(new Blob([report], { type: 'application/json' }));
                const anchor = document.createElement('a');
                anchor.href = url;
                anchor.download = 'tv-acceptance.json';
                anchor.click();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
              }}
            >
              下載 JSON
            </button>
            <p>若 TV 不支援下載，可拍攝結果或選取文字複製；下載不代表已傳送報告。</p>
          </section>
        )}
      </div>
    </main>
  );
}
